import React, { useState, useRef, useCallback } from 'react';
import {
  X, ChevronLeft, ChevronRight, Plus, Trash2, Upload, Check, Copy,
  Image as ImageIcon, Palette, Type, FileText, MessageCircle,
  Shapes, Layers, Bookmark, BarChart3, LayoutTemplate,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { EDITOR_FONTS, loadGoogleFont } from '../../lib/fonts';

// ═══ STORAGE ═══════════════════════════════════════════════════════════════════

const STORAGE_KEY = 'ivisora_brand_kit';

const EMPTY_KIT = {
  colors:     [],   // [{ id, name, hex }]
  fonts:      [],   // [{ id, name, role: 'heading'|'body' }]
  logos:      [],   // [{ id, name, url }]
  photos:     [],   // [{ id, name, url }]
  graphics:   [],   // [{ id, name, url }]
  icons:      [],   // [{ id, name, url }]
  guidelines: '',
  brandVoice: { tones: [], notes: '' },
  chartStyle: null,
};

export const loadBrandKitFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...EMPTY_KIT };
    const parsed = JSON.parse(raw);
    // Migrate old format (no photos/graphics/icons/guidelines/brandVoice/chartStyle)
    return { ...EMPTY_KIT, ...parsed };
  } catch {
    return { ...EMPTY_KIT };
  }
};

export const saveBrandKitToStorage = (kit) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(kit)); } catch {}
};

// ═══ CONSTANTS ═════════════════════════════════════════════════════════════════

const mkId = () => `b-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const SECTIONS = [
  { id: 'all',        label: 'All Assets',  Icon: LayoutTemplate },
  { id: 'guidelines', label: 'Guidelines',  Icon: FileText       },
  { id: 'logos',      label: 'Logos',       Icon: Bookmark       },
  { id: 'colors',     label: 'Colors',      Icon: Palette        },
  { id: 'fonts',      label: 'Fonts',       Icon: Type           },
  { id: 'brandVoice', label: 'Brand Voice', Icon: MessageCircle  },
  { id: 'photos',     label: 'Photos',      Icon: ImageIcon      },
  { id: 'graphics',   label: 'Graphics',    Icon: Shapes         },
  { id: 'icons',      label: 'Icons',       Icon: Layers         },
  { id: 'charts',     label: 'Charts',      Icon: BarChart3      },
];

const COMMON_FONTS = EDITOR_FONTS;

const TONES = [
  'Friendly', 'Professional', 'Modern', 'Corporate',
  'Creative', 'Playful', 'Elegant', 'Bold', 'Minimal', 'Luxurious',
];

const CHART_STYLES = [
  { id: 'minimal',    label: 'Minimal',    desc: 'Clean lines, light fill'       },
  { id: 'bold',       label: 'Bold',       desc: 'High contrast, vivid tones'    },
  { id: 'rounded',    label: 'Rounded',    desc: 'Soft corners, friendly feel'   },
  { id: 'monochrome', label: 'Monochrome', desc: 'Single hue with tints'         },
  { id: 'gradient',   label: 'Gradient',   desc: 'Smooth color transitions'      },
];

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
const MAX_MB = 5;

// ═══ SHARED MICRO-COMPONENTS ═══════════════════════════════════════════════════

const SecLabel = ({ children }) => (
  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{children}</p>
);

const EmptyCard = ({ Icon, label, sub }) => (
  <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
    <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
      <Icon className="w-5 h-5 text-gray-300" />
    </div>
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Reusable upload + grid for logos / photos / graphics / icons ───────────────

const UploadGrid = ({ items, onAdd, onDelete, onInsert, accept = 'image/*', label = 'Upload', toast }) => {
  const inputRef   = useRef(null);
  const [isDrag, setIsDrag] = useState(false);

  const processFile = useCallback((file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast({ title: 'Unsupported format', description: 'Use PNG, JPG, SVG, GIF or WebP.', variant: 'destructive' });
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({ title: `File exceeds ${MAX_MB} MB`, variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) =>
      onAdd({ id: mkId(), name: file.name.replace(/\.\w+$/, ''), url: ev.target.result });
    reader.readAsDataURL(file);
  }, [onAdd, toast]);

  const handleFiles = (files) => Array.from(files).forEach(processFile);

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        onDragEnter={(e) => { e.preventDefault(); setIsDrag(true); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDrag(false); }}
        onDrop={(e) => { e.preventDefault(); setIsDrag(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`
          flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed
          py-6 cursor-pointer transition-all duration-200
          ${isDrag ? 'border-violet-500 bg-violet-50 scale-[0.98]' : 'border-gray-200 hover:border-violet-400 hover:bg-violet-50/40'}
        `}
      >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isDrag ? 'bg-violet-200' : 'bg-gray-100'}`}>
          <Upload className={`w-5 h-5 ${isDrag ? 'text-violet-600' : 'text-gray-400'}`} />
        </div>
        <div className="text-center">
          <p className="text-xs font-medium text-gray-600">{isDrag ? 'Drop to upload' : label}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">PNG, JPG, SVG up to {MAX_MB} MB</p>
        </div>
        <input
          ref={inputRef} type="file" accept={accept} multiple
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
          className="hidden"
        />
      </div>

      {/* Asset grid */}
      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {items.map((item) => (
            <div
              key={item.id}
              className="group relative rounded-xl overflow-hidden border border-gray-100 bg-gray-50 aspect-square cursor-pointer"
              onClick={() => onInsert(item)}
            >
              <img
                src={item.url} alt={item.name}
                className="w-full h-full object-contain transition-transform duration-200 group-hover:scale-105"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-1.5">
                <span className="text-white text-[9px] font-medium text-center leading-tight line-clamp-2">{item.name}</span>
                <div className="flex gap-1 mt-0.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); onInsert(item); }}
                    className="px-2 py-0.5 bg-white/25 hover:bg-white/40 text-white text-[9px] rounded-md transition-colors"
                  >
                    Insert
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                    className="px-2 py-0.5 bg-red-500/80 hover:bg-red-500 text-white text-[9px] rounded-md transition-colors"
                  >
                    Del
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══ MAIN COMPONENT ════════════════════════════════════════════════════════════

const BrandKitPanel = ({ brandKit, onBrandKitChange, onAddElement, onClose, onApplyBrand }) => {
  const { toast } = useToast();
  const [section, setSection] = useState('all');

  // ── Core update helper ──────────────────────────────────────────────────────

  const update = useCallback((patch) => {
    const next = { ...brandKit, ...patch };
    onBrandKitChange?.(next);
    saveBrandKitToStorage(next);
  }, [brandKit, onBrandKitChange]);

  // ── List helpers ────────────────────────────────────────────────────────────

  const addToList   = (key, item)  => update({ [key]: [...(brandKit[key] || []), item] });
  const removeFromList = (key, id) => update({ [key]: (brandKit[key] || []).filter(i => i.id !== id) });

  const insertAsset = (item, size = 300) =>
    onAddElement?.({ type: 'image', src: item.url, x: 50, y: 50, width: size, height: size });

  // ── Color form state ────────────────────────────────────────────────────────

  const [newHex,  setNewHex]  = useState('#6366f1');
  const [newName, setNewName] = useState('');

  const addColor = () => {
    addToList('colors', { id: mkId(), name: newName.trim() || newHex, hex: newHex });
    setNewName('');
  };

  // ── Font helpers ────────────────────────────────────────────────────────────

  const getFont   = (role) => (brandKit.fonts || []).find(f => f.role === role)?.name || '';
  const setFont   = (role, name) => {
    loadGoogleFont(name);
    const others = (brandKit.fonts || []).filter(f => f.role !== role);
    update({ fonts: name ? [...others, { id: mkId(), name, role }] : others });
  };

  // ── Brand Voice ─────────────────────────────────────────────────────────────

  const bv = brandKit.brandVoice || { tones: [], notes: '' };
  const toggleTone = (tone) => {
    const tones = bv.tones.includes(tone) ? bv.tones.filter(t => t !== tone) : [...bv.tones, tone];
    update({ brandVoice: { ...bv, tones } });
  };

  // ── Section badge counts ────────────────────────────────────────────────────

  const countFor = (id) => {
    const k = brandKit || {};
    if (id === 'guidelines') return k.guidelines ? 1 : 0;
    if (id === 'brandVoice') return (k.brandVoice?.tones?.length || 0) + (k.brandVoice?.notes ? 1 : 0);
    if (id === 'fonts')      return (k.fonts  || []).length;
    if (id === 'colors')     return (k.colors || []).length;
    if (id === 'charts')     return k.chartStyle ? 1 : 0;
    return (k[id] || []).length;
  };

  const current  = SECTIONS.find(s => s.id === section);
  const isHome   = section === 'all';
  const headingF = getFont('heading');
  const bodyF    = getFont('body');
  const brandColors = (brandKit.colors || []).map(c => c.hex);
  const previewCols = brandColors.length >= 3 ? brandColors.slice(0, 3) : ['#6366f1', '#ec4899', '#f97316'];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col bg-white select-none">

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!isHome && (
              <button
                onClick={() => setSection('all')}
                className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shadow-sm shrink-0 ${
              isHome ? 'bg-gradient-to-br from-violet-500 to-fuchsia-500' : 'bg-gray-100'
            }`}>
              {isHome
                ? <Bookmark className="w-3.5 h-3.5 text-white" />
                : <current.Icon className="w-3.5 h-3.5 text-gray-600" />
              }
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm leading-tight">
                {isHome ? 'Brand Kit' : current.label}
              </h3>
              <p className="text-[10px] text-gray-400 leading-tight">
                {isHome ? 'Your brand assets' : 'Brand Kit'}
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ── Scrollable body ──────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto overscroll-y-contain min-h-0">

        {/* ════════ HOME / ALL ASSETS ════════ */}
        {isHome && (
          <div className="p-4 space-y-4">

            {/* Apply Brand CTA — only shows when there's something to apply */}
            {onApplyBrand && ((brandKit.colors?.length || 0) + (brandKit.fonts?.length || 0)) > 0 && (
              <button
                onClick={() => { onApplyBrand(brandKit); toast({ title: 'Brand applied!', description: 'Fonts and colors updated across the canvas.' }); }}
                className="w-full h-10 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white text-sm font-semibold tracking-wide transition-all shadow-sm shadow-violet-200/60 flex items-center justify-center gap-2"
              >
                <Bookmark className="w-4 h-4" /> Apply Brand
              </button>
            )}

            {/* Quick peek — colors */}
            {(brandKit.colors || []).length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <SecLabel>Colors</SecLabel>
                  <button onClick={() => setSection('colors')} className="text-[10px] text-violet-500 hover:text-violet-700 font-medium">Manage</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(brandKit.colors || []).slice(0, 12).map(c => (
                    <div key={c.id} title={`${c.name} ${c.hex}`}
                      className="w-7 h-7 rounded-lg border-2 border-white shadow ring-1 ring-gray-200"
                      style={{ backgroundColor: c.hex }} />
                  ))}
                </div>
              </div>
            )}

            {/* Quick peek — fonts */}
            {(brandKit.fonts || []).length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <SecLabel>Fonts</SecLabel>
                  <button onClick={() => setSection('fonts')} className="text-[10px] text-violet-500 hover:text-violet-700 font-medium">Manage</button>
                </div>
                <div className="space-y-1">
                  {(brandKit.fonts || []).map(f => (
                    <div key={f.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-100">
                      <Type className="w-3 h-3 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-700 font-medium" style={{ fontFamily: f.name }}>{f.name}</span>
                      <span className="ml-auto text-[10px] text-gray-400 capitalize bg-white border border-gray-200 px-1.5 py-0.5 rounded-full">{f.role}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick peek — logos */}
            {(brandKit.logos || []).length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <SecLabel>Logos</SecLabel>
                  <button onClick={() => setSection('logos')} className="text-[10px] text-violet-500 hover:text-violet-700 font-medium">Manage</button>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {(brandKit.logos || []).slice(0, 4).map(l => (
                    <div key={l.id} className="aspect-square rounded-lg bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center">
                      <img src={l.url} alt={l.name} className="max-w-full max-h-full object-contain" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section navigation list */}
            <div>
              <SecLabel>All Sections</SecLabel>
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                {SECTIONS.filter(s => s.id !== 'all').map((s, idx, arr) => {
                  const count = countFor(s.id);
                  return (
                    <button key={s.id} onClick={() => setSection(s.id)}
                      className={`w-full px-3 py-2.5 flex items-center gap-3 text-left hover:bg-gray-50 transition-colors ${idx < arr.length - 1 ? 'border-b border-gray-50' : ''}`}
                    >
                      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                        <s.Icon className="w-3.5 h-3.5 text-gray-500" />
                      </div>
                      <span className="flex-1 text-sm font-medium text-gray-700">{s.label}</span>
                      {count > 0 && (
                        <span className="text-[10px] font-bold text-violet-600 bg-violet-100 rounded-full px-1.5 py-0.5 tabular-nums">{count}</span>
                      )}
                      <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ════════ GUIDELINES ════════ */}
        {section === 'guidelines' && (
          <div className="p-4 space-y-3">
            <SecLabel>Brand Usage Guidelines</SecLabel>
            <p className="text-[11px] text-gray-400 -mt-1">Document rules for logo, color, and typography usage.</p>
            <textarea
              value={brandKit.guidelines || ''}
              onChange={(e) => update({ guidelines: e.target.value })}
              placeholder={"Logo Usage Rules\n• Always maintain clear space\n• Never stretch or distort\n\nTypography Rules\n• Use brand fonts only\n\nColor Usage Rules\n• Primary color for CTAs only\n\nBrand Spacing Rules\n• Minimum padding: 16px"}
              rows={16}
              className="w-full px-3 py-2.5 text-sm text-gray-700 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100 transition-all resize-none leading-relaxed placeholder:text-gray-300"
            />
            <p className="text-[10px] text-gray-400 text-center">Auto-saved as you type</p>
          </div>
        )}

        {/* ════════ LOGOS ════════ */}
        {section === 'logos' && (
          <div className="p-4 space-y-3">
            <SecLabel>Brand Logos</SecLabel>
            <UploadGrid
              items={brandKit.logos || []}
              onAdd={(item) => addToList('logos', item)}
              onDelete={(id) => removeFromList('logos', id)}
              onInsert={(item) => {
                const img = new window.Image();
                img.onload = () => insertAsset({ ...item }, Math.min(img.naturalWidth, 600));
                img.onerror = () => insertAsset(item, 300);
                img.src = item.url;
                toast({ title: `${item.name} added to canvas` });
              }}
              accept="image/png,image/svg+xml,image/jpeg"
              label="Upload Logo (PNG, SVG, JPG)"
              toast={toast}
            />
            {(brandKit.logos || []).length === 0 && (
              <EmptyCard Icon={Bookmark} label="No logos yet" sub="Upload your brand logos" />
            )}
          </div>
        )}

        {/* ════════ COLORS ════════ */}
        {section === 'colors' && (
          <div className="p-4 space-y-4">
            <SecLabel>Brand Colors</SecLabel>

            {/* Add color form */}
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-600">Add Color</p>
              <div className="flex gap-2 items-center">
                {/* Swatch picker */}
                <label className="shrink-0 cursor-pointer">
                  <div
                    className="w-9 h-9 rounded-lg border-2 border-white shadow ring-1 ring-gray-200 hover:scale-105 transition-transform"
                    style={{ backgroundColor: newHex }}
                  />
                  <input type="color" value={newHex} onChange={(e) => setNewHex(e.target.value)} className="sr-only" />
                </label>
                {/* Hex input */}
                <div className="flex items-center gap-1 flex-1 h-9 px-2.5 rounded-lg border border-gray-200 bg-white focus-within:border-violet-400 focus-within:ring-2 focus-within:ring-violet-100 transition-all">
                  <span className="text-gray-400 text-xs font-mono select-none">#</span>
                  <input
                    type="text"
                    value={newHex.replace('#', '').toUpperCase()}
                    onChange={(e) => {
                      const h = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                      if (h.length === 6) setNewHex(`#${h}`);
                    }}
                    className="flex-1 bg-transparent text-xs font-mono text-gray-800 outline-none w-0"
                    maxLength={6} spellCheck={false}
                  />
                </div>
                <button
                  onClick={addColor}
                  className="w-9 h-9 shrink-0 rounded-lg bg-violet-600 hover:bg-violet-700 text-white flex items-center justify-center transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text" value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addColor()}
                placeholder="Color name (e.g. Primary, Accent)"
                className="w-full h-8 px-2.5 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all text-gray-700"
              />
            </div>

            {/* Color list */}
            {(brandKit.colors || []).length > 0 ? (
              <div className="space-y-2">
                {(brandKit.colors || []).map((c) => (
                  <div key={c.id} className="flex items-center gap-2.5 p-2 rounded-xl bg-gray-50 border border-gray-100 group">
                    <div className="w-9 h-9 rounded-lg border-2 border-white shadow ring-1 ring-gray-200 shrink-0" style={{ backgroundColor: c.hex }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">{c.name}</p>
                      <p className="text-[10px] font-mono text-gray-400 uppercase">{c.hex}</p>
                    </div>
                    <button
                      onClick={() => { navigator.clipboard?.writeText(c.hex); toast({ title: `${c.hex} copied` }); }}
                      className="w-7 h-7 rounded-md opacity-0 group-hover:opacity-100 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-all"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => removeFromList('colors', c.id)}
                      className="w-7 h-7 rounded-md opacity-0 group-hover:opacity-100 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyCard Icon={Palette} label="No colors yet" sub="Add your brand color palette" />
            )}
          </div>
        )}

        {/* ════════ FONTS ════════ */}
        {section === 'fonts' && (
          <div className="p-4 space-y-4">
            <SecLabel>Brand Typography</SecLabel>

            {/* Heading font */}
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 space-y-2">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Heading Font</p>
              <select
                value={headingF}
                onChange={(e) => setFont('heading', e.target.value)}
                className="w-full h-9 px-2.5 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all text-gray-800 cursor-pointer"
              >
                <option value="">— Select heading font —</option>
                {COMMON_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              {headingF && (
                <p className="text-xl font-bold text-gray-800 leading-snug pt-1" style={{ fontFamily: headingF }}>
                  The quick brown fox
                </p>
              )}
            </div>

            {/* Body font */}
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 space-y-2">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Body Font</p>
              <select
                value={bodyF}
                onChange={(e) => setFont('body', e.target.value)}
                className="w-full h-9 px-2.5 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all text-gray-800 cursor-pointer"
              >
                <option value="">— Select body font —</option>
                {COMMON_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              {bodyF && (
                <p className="text-sm text-gray-600 leading-relaxed pt-1" style={{ fontFamily: bodyF }}>
                  Consistent typography builds brand recognition across all design touchpoints.
                </p>
              )}
            </div>

            {/* All set fonts */}
            {(brandKit.fonts || []).length > 0 && (
              <div>
                <SecLabel>Active Brand Fonts</SecLabel>
                <div className="space-y-1.5">
                  {(brandKit.fonts || []).map(f => (
                    <div key={f.id} className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl bg-gray-50 border border-gray-100 group">
                      <Type className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="flex-1 text-sm font-medium text-gray-700 truncate" style={{ fontFamily: f.name }}>{f.name}</span>
                      <span className="text-[10px] capitalize text-gray-400 bg-white border border-gray-200 px-1.5 py-0.5 rounded-full">{f.role}</span>
                      <button
                        onClick={() => removeFromList('fonts', f.id)}
                        className="w-6 h-6 rounded-md opacity-0 group-hover:opacity-100 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ════════ BRAND VOICE ════════ */}
        {section === 'brandVoice' && (
          <div className="p-4 space-y-4">
            <SecLabel>Brand Tone</SecLabel>
            <p className="text-[11px] text-gray-400 -mt-1">Select tone descriptors for your brand.</p>
            <div className="flex flex-wrap gap-1.5">
              {TONES.map((tone) => {
                const active = (bv.tones || []).includes(tone);
                return (
                  <button
                    key={tone} onClick={() => toggleTone(tone)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all flex items-center gap-1 ${
                      active
                        ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-violet-400 hover:text-violet-600'
                    }`}
                  >
                    {active && <Check className="w-3 h-3" />}
                    {tone}
                  </button>
                );
              })}
            </div>

            <div>
              <SecLabel>Brand Voice Notes</SecLabel>
              <textarea
                value={bv.notes || ''}
                onChange={(e) => update({ brandVoice: { ...bv, notes: e.target.value } })}
                placeholder="Describe your brand communication style, do's and don'ts, example phrases..."
                rows={8}
                className="w-full px-3 py-2.5 text-sm text-gray-700 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100 transition-all resize-none leading-relaxed placeholder:text-gray-300"
              />
            </div>
            <p className="text-[10px] text-gray-400 text-center">Auto-saved as you type</p>
          </div>
        )}

        {/* ════════ PHOTOS ════════ */}
        {section === 'photos' && (
          <div className="p-4 space-y-3">
            <SecLabel>Brand Photography</SecLabel>
            <UploadGrid
              items={brandKit.photos || []}
              onAdd={(item) => addToList('photos', item)}
              onDelete={(id) => removeFromList('photos', id)}
              onInsert={(item) => { insertAsset(item, 400); toast({ title: `${item.name} added to canvas` }); }}
              label="Upload Brand Photo"
              toast={toast}
            />
            {(brandKit.photos || []).length === 0 && (
              <EmptyCard Icon={ImageIcon} label="No photos yet" sub="Upload brand photography" />
            )}
          </div>
        )}

        {/* ════════ GRAPHICS ════════ */}
        {section === 'graphics' && (
          <div className="p-4 space-y-3">
            <SecLabel>Brand Graphics & Illustrations</SecLabel>
            <UploadGrid
              items={brandKit.graphics || []}
              onAdd={(item) => addToList('graphics', item)}
              onDelete={(id) => removeFromList('graphics', id)}
              onInsert={(item) => { insertAsset(item, 350); toast({ title: `${item.name} added to canvas` }); }}
              label="Upload Graphic or Illustration"
              toast={toast}
            />
            {(brandKit.graphics || []).length === 0 && (
              <EmptyCard Icon={Shapes} label="No graphics yet" sub="Upload illustrations, shapes, decorations" />
            )}
          </div>
        )}

        {/* ════════ ICONS ════════ */}
        {section === 'icons' && (
          <div className="p-4 space-y-3">
            <SecLabel>Brand Icons</SecLabel>
            <UploadGrid
              items={brandKit.icons || []}
              onAdd={(item) => addToList('icons', item)}
              onDelete={(id) => removeFromList('icons', id)}
              onInsert={(item) => { onAddElement?.({ type: 'image', src: item.url, x: 50, y: 50, width: 80, height: 80 }); toast({ title: `${item.name} added to canvas` }); }}
              accept="image/svg+xml,image/png"
              label="Upload Icon (SVG, PNG)"
              toast={toast}
            />
            {(brandKit.icons || []).length === 0 && (
              <EmptyCard Icon={Layers} label="No icons yet" sub="Upload brand icon sets" />
            )}
          </div>
        )}

        {/* ════════ CHARTS ════════ */}
        {section === 'charts' && (
          <div className="p-4 space-y-4">
            <SecLabel>Chart Style</SecLabel>
            <p className="text-[11px] text-gray-400 -mt-1">Select a visual style — brand colors are applied automatically.</p>
            <div className="space-y-2">
              {CHART_STYLES.map((style) => {
                const isActive = brandKit.chartStyle === style.id;
                return (
                  <button
                    key={style.id}
                    onClick={() => update({ chartStyle: style.id })}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                      isActive
                        ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-100'
                        : 'border-gray-100 bg-gray-50/60 hover:border-violet-300 hover:bg-violet-50/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Mini bar chart preview */}
                      <div className="flex items-end gap-0.5 h-8 shrink-0">
                        {[60, 80, 45, 70, 55].map((h, i) => (
                          <div
                            key={i}
                            className={style.id === 'rounded' ? 'w-3 rounded-full' : 'w-3 rounded-sm'}
                            style={{
                              height: `${h}%`,
                              opacity: style.id === 'minimal' ? 0.65 : 1,
                              background: style.id === 'gradient'
                                ? `linear-gradient(to top, ${previewCols[i % previewCols.length]}, ${previewCols[(i + 1) % previewCols.length]})`
                                : style.id === 'monochrome'
                                  ? previewCols[0]
                                  : previewCols[i % previewCols.length],
                              filter: style.id === 'monochrome' ? `opacity(${0.4 + i * 0.15})` : 'none',
                            }}
                          />
                        ))}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-800">{style.label}</p>
                          {isActive && <Check className="w-3.5 h-3.5 text-violet-600" />}
                        </div>
                        <p className="text-[11px] text-gray-500">{style.desc}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <div className="shrink-0 px-4 py-2 border-t border-gray-100 bg-gray-50/60">
        <p className="text-[10px] text-gray-400 text-center">
          {isHome ? 'Brand assets saved automatically' : `${current.label} · Brand Kit`}
        </p>
      </div>
    </div>
  );
};

export default BrandKitPanel;
